import { useMemo } from 'react'
import {
  isCommentVisibleForCommentFilter,
  isCommentVisibleForStoreys,
  isPlanConfigVisibleForCommentFilter,
} from '@constants'
import useGetPlanSettings from '@editor/hooks/requests/useGetPlanSettings'
import usePlanViewPlanTables from '@editor/pages/PlanView/hooks/usePlanViewPlanTables'
import {
  buildOrthoSnapLines,
  ImmutableVector3,
  LineTarget,
  PointTarget,
  snapTargetWeights,
} from '@modugen/ui-toolkit'
import {
  useCommentFilterStore,
  useCommentsStore,
  useModelStore,
  usePlanImageCardStore,
  usePlanTableStore,
} from '@editorStores'
import { usePlanTableLayoutMetrics } from '@editorHooks'
import { computePlanImageCardLayoutDimensions } from 'src/domain/planImageCards/layout'
import { computePlanTableBounds } from 'src/domain/planTables/positionAnchor'
import { computeTableLayoutDimensions } from 'src/domain/planTables/tableLayout'

interface SnapPoint {
  x: number
  y: number
  z: number
}

/** Corners + center of an axis-aligned rect, at world `z`. */
const boxSnapPoints = (
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  z: number,
): SnapPoint[] => [
  { x: xMin, y: yMax, z },
  { x: xMax, y: yMax, z },
  { x: xMin, y: yMin, z },
  { x: xMax, y: yMin, z },
  { x: (xMin + xMax) / 2, y: (yMin + yMax) / 2, z },
]

interface Options {
  /** "an Elementen ausrichten" – snap directly to another item's corners/center/position. */
  snapToCornersAndEdges?: boolean
  /** "orthogonal an Elementen ausrichten" – also snap to their horizontal/vertical extension lines. */
  orthoSnap?: boolean
}

export interface PlanPositionSnapTargets {
  pointTargets: PointTarget[]
  /** Horizontal/vertical extension lines through the point targets, present only when `orthoSnap`. */
  lineTargets: LineTarget[]
}

/**
 * Drag-snap targets for plan tables, plan images and comments: the corners +
 * center of every visible table/image, and the position (its only reference
 * point) of every visible comment. `excludeId` leaves out the item currently
 * being dragged so it never snaps to itself. `snapToCornersAndEdges` gates
 * `pointTargets` themselves; `orthoSnap` additionally builds `lineTargets` –
 * the horizontal/vertical extension line through each point – so a drag can
 * align with another item's edge/center without landing exactly on it.
 */
const usePlanPositionSnapTargets = (
  excludeId?: string | null,
  { snapToCornersAndEdges = false, orthoSnap = false }: Options = {},
): PlanPositionSnapTargets => {
  const tables = usePlanTableStore(state => state.tables)
  const activeFilterViewId = usePlanTableStore(state => state.activeFilterViewId)
  const tablesVisible = usePlanTableStore(state => state.isVisible)
  const cards = usePlanImageCardStore(state => state.cards)
  const cardsVisible = usePlanImageCardStore(state => state.isVisible)
  const imageAspectRatios = usePlanImageCardStore(state => state.imageAspectRatios)
  const comments = useCommentsStore(state => state.comments)
  const showComments = useModelStore(state => state.showComments)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const tableLayoutMetrics = usePlanTableLayoutMetrics()
  const { getTableData, isExportedInPreview } = usePlanViewPlanTables()
  const { data: planSettings } = useGetPlanSettings()
  const planScale = planSettings?.scale ?? 100

  const pointTargets = useMemo(() => {
    const points: SnapPoint[] = []

    if (tablesVisible) {
      tables.forEach(table => {
        if (table.id === excludeId) return
        if (table.filterViewId !== activeFilterViewId) return
        if (!isPlanConfigVisibleForCommentFilter(table.planConfig, selectedPlan)) return
        if (!isExportedInPreview(table)) return
        const dims = computeTableLayoutDimensions(getTableData(table), tableLayoutMetrics)
        const bounds = computePlanTableBounds(
          table.position,
          { width: dims.totalWidth, height: dims.totalHeight },
          table.positionAnchor,
        )
        points.push(
          ...boxSnapPoints(bounds.xMin, bounds.xMax, bounds.yMin, bounds.yMax, table.position.z),
        )
      })
    }

    if (cardsVisible) {
      cards.forEach(card => {
        if (card.id === excludeId) return
        if (!isPlanConfigVisibleForCommentFilter(card.planConfig, selectedPlan)) return
        const dims = computePlanImageCardLayoutDimensions(card, {
          planScale,
          imageAspectRatio: imageAspectRatios[card.id] ?? null,
        })
        const { x, y, z } = card.position
        points.push(...boxSnapPoints(x, x + dims.width, y - dims.totalHeight, y, z))
      })
    }

    if (showComments) {
      comments.forEach(comment => {
        if (comment.id === excludeId) return
        if (!isCommentVisibleForCommentFilter(comment, selectedPlan)) return
        if (!isCommentVisibleForStoreys(comment, visibleStoreys)) return
        const { x, y, z } = comment.position
        points.push({ x, y, z })
      })
    }

    return points.map(
      (point): PointTarget => ({
        point: new ImmutableVector3(point.x, point.y, point.z),
        weight: snapTargetWeights.points,
      }),
    )
  }, [
    tables,
    tablesVisible,
    activeFilterViewId,
    cards,
    cardsVisible,
    imageAspectRatios,
    comments,
    showComments,
    visibleStoreys,
    selectedPlan,
    tableLayoutMetrics,
    getTableData,
    isExportedInPreview,
    planScale,
    excludeId,
  ])

  return useMemo(
    (): PlanPositionSnapTargets => ({
      pointTargets: snapToCornersAndEdges ? pointTargets : [],
      lineTargets: orthoSnap && pointTargets.length > 0 ? buildOrthoSnapLines(pointTargets) : [],
    }),
    [pointTargets, snapToCornersAndEdges, orthoSnap],
  )
}

export default usePlanPositionSnapTargets
